<script setup lang="ts">
import type { NuxtError } from "#app";
import { fr } from "@nuxt/ui/locale";

const props = defineProps<{ error: NuxtError }>();

const { t } = useI18n();

const isNotFound = computed(() => props.error.status === 404);
const isDev = import.meta.dev;

const title = computed(() => (isNotFound.value ? t("error.notFound.header") : t("error.default.header")));
const message = computed(() => (isNotFound.value ? t("error.notFound.message") : t("error.default.message")));

useHead({
  title: () => title.value,
  titleTemplate: (chunk) => formatDocumentTitle(chunk ?? undefined, t("app.title")),
});

onMounted(() => {
  console.error("[nuxt-error]", props.error.status, props.error.message, props.error.stack);
});

const goHome = () => clearError({ redirect: "/" });
const reload = () => {
  if (import.meta.client) window.location.reload();
};
</script>

<template>
  <UApp :locale="fr">
    <NuxtLayout name="app">
      <UContainer class="flex flex-col grow bg-blue-100 py-[5%]">
        <div data-testid="error-page" class="flex flex-col items-center justify-center grow px-4 py-16 mx-auto max-w-lg text-center">
          <header class="mb-10">
            <p data-testid="error-status" class="text-8xl font-mono text-red-300 mb-2">{{ error.status }}</p>
            <h1 data-testid="error-title" class="text-4xl md:text-4xl font-semibold mb-3">{{ title }}</h1>
            <p data-testid="error-message" class="text-base text-gray-700 whitespace-pre-wrap wrap-break-words">{{ message }}</p>
          </header>

          <details v-if="isDev && error.stack" class="text-sm text-left border border-gray-500 text-gray-500 rounded p-2 mb-10 w-full">
            <summary class="cursor-pointer">Stack trace</summary>
            <pre class="whitespace-pre-wrap wrap-break-words mt-2 leading-normal">{{ error.stack }}</pre>
          </details>

          <div class="flex flex-col justify-center text-center sm:flex-row gap-3 w-full">
            <UButton data-testid="error-action-home" color="secondary" variant="solid" @click="goHome">
              {{ $t("error.actions.home") }}
            </UButton>
            <UButton v-if="!isNotFound" data-testid="error-action-reload" color="primary" variant="outline" @click="reload">
              {{ $t("error.actions.reload") }}
            </UButton>
          </div>
        </div>
      </UContainer>
    </NuxtLayout>
  </UApp>
</template>
